import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiService from "../services/api";

type AnswerValue = number;

type AnswersByCategory = Record<string, Record<string, AnswerValue>>;

type PendingEntry = {
  value: AnswerValue;
  updatedAt: string;
};

type PendingMap = Record<string, Record<string, PendingEntry>>;

type LastSavedMap = Record<string, string | null>;

const getInitialOnlineStatus = () => {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
};

const canUseStorage = () => typeof window !== "undefined" && !!window.localStorage;

const persistStorage = (
  storageKey: string | null,
  payload: { answers: AnswersByCategory; pending: PendingMap; lastSavedAt: LastSavedMap },
) => {
  if (!storageKey || !canUseStorage()) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (error) {
    console.error("[Compliance] failed to persist answers to storage", error);
  }
};

const loadStorage = (storageKey: string | null) => {
  if (!storageKey || !canUseStorage()) {
    return {
      answers: {} as AnswersByCategory,
      pending: {} as PendingMap,
      lastSavedAt: {} as LastSavedMap,
    };
  }

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return {
        answers: {} as AnswersByCategory,
        pending: {} as PendingMap,
        lastSavedAt: {} as LastSavedMap,
      };
    }
    const parsed = JSON.parse(raw);
    return {
      answers: (parsed?.answers ?? {}) as AnswersByCategory,
      pending: (parsed?.pending ?? {}) as PendingMap,
      lastSavedAt: (parsed?.lastSavedAt ?? {}) as LastSavedMap,
    };
  } catch (error) {
    console.error("[Compliance] failed to parse stored answers", error);
    return {
      answers: {} as AnswersByCategory,
      pending: {} as PendingMap,
      lastSavedAt: {} as LastSavedMap,
    };
  }
};

interface UseComplianceAnswersOptions {
  onSurveySaved?: () => void;
  questionnaireType?: string;
}

export interface UseComplianceAnswersResult {
  answers: AnswersByCategory;
  setAnswer: (categoryId: string, questionId: string, value: number) => void;
  saveAnswers: (categoryId?: string) => Promise<boolean>;
  hydrateAnswers: (grouped: AnswersByCategory) => void;
  registerCategories: (
    categories: Array<{
      id: string;
      code?: string | null;
      name?: string | null;
      questions?: Array<{ id: string }>;
    }>,
  ) => void;
  isSaving: Record<string, boolean>;
  lastSavedAt: LastSavedMap;
  pendingCategories: string[];
  hasUnsavedChanges: boolean;
  isOnline: boolean;
}

export function useComplianceAnswers(
  organizationId?: string | null,
  standardCode?: string | null,
  options: UseComplianceAnswersOptions = {},
): UseComplianceAnswersResult {
  const storageKey = useMemo(() => {
    if (!organizationId || !standardCode) return null;
    return `compliance_answers_${organizationId}_${standardCode}`.toLowerCase();
  }, [organizationId, standardCode]);

  const [{ answers, pending, lastSavedAt }, setState] = useState(() =>
    loadStorage(storageKey),
  );

  const answersRef = useRef<AnswersByCategory>(answers);
  const pendingRef = useRef<PendingMap>(pending);
  const lastSavedRef = useRef<LastSavedMap>(lastSavedAt);
  const categoriesRef = useRef<Record<string, { id: string; code?: string | null; name?: string | null }>>({});
  const [isSavingMap, setIsSavingMap] = useState<Record<string, boolean>>({});
  const isSavingRef = useRef<Record<string, boolean>>({});
  const [isOnline, setIsOnline] = useState<boolean>(getInitialOnlineStatus());


  const buildSurveySubmission = useCallback(() => {
    const allAnswers = answersRef.current;
    const categoryLookup = categoriesRef.current;

    const mergedAnswers: Record<string, number> = {};
    const categoryScores: Record<string, number> = {};

    let totalValue = 0;
    let totalCount = 0;

    Object.entries(allAnswers).forEach(([categoryId, questionMap]) => {
      let categorySum = 0;
      let categoryCount = 0;

      Object.entries(questionMap).forEach(([questionId, value]) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) return;
        mergedAnswers[questionId] = numeric;
        categorySum += numeric;
        categoryCount += 1;
        totalValue += numeric;
        totalCount += 1;
      });

      if (categoryCount > 0) {
        const categoryMeta = categoryLookup[categoryId];
        const key = categoryMeta?.code || categoryId;
        categoryScores[key] = Math.round((categorySum / categoryCount) * 20);
      }
    });

    if (totalCount === 0) {
      return null;
    }

    const complianceScore = Number((totalValue / totalCount).toFixed(2));

    return {
      answers: mergedAnswers,
      scores: categoryScores,
      complianceScore,
    };
  }, []);

  useEffect(() => {
    answersRef.current = answers;
    pendingRef.current = pending;
    lastSavedRef.current = lastSavedAt;
  }, [answers, pending, lastSavedAt]);

  useEffect(() => {
    if (!storageKey) {
      setState({
        answers: {},
        pending: {},
        lastSavedAt: {},
      });
      answersRef.current = {};
      pendingRef.current = {};
      lastSavedRef.current = {};
      return;
    }

    const snapshot = loadStorage(storageKey);
    setState(snapshot);
    answersRef.current = snapshot.answers;
    pendingRef.current = snapshot.pending;
    lastSavedRef.current = snapshot.lastSavedAt;
  }, [storageKey]);

  const updateState = useCallback(
    (next: {
      answers?: AnswersByCategory;
      pending?: PendingMap;
      lastSavedAt?: LastSavedMap;
    }) => {
      const nextAnswers = next.answers ?? answersRef.current;
      const nextPending = next.pending ?? pendingRef.current;
      const nextLastSaved = next.lastSavedAt ?? lastSavedRef.current;

      answersRef.current = nextAnswers;
      pendingRef.current = nextPending;
      lastSavedRef.current = nextLastSaved;

      setState({
        answers: nextAnswers,
        pending: nextPending,
        lastSavedAt: nextLastSaved,
      });

      persistStorage(storageKey, {
        answers: nextAnswers,
        pending: nextPending,
        lastSavedAt: nextLastSaved,
      });
    },
    [storageKey],
  );

  const clearSavingFlags = useCallback((categoryIds: string[]) => {
    setIsSavingMap((prev) => {
      const next = { ...prev };
      categoryIds.forEach((id) => {
        next[id] = false;
      });
      isSavingRef.current = next;
      return next;
    });
  }, []);

  const flushPending = useCallback(
    async (targetCategories?: string[]) => {
      if (!organizationId || !standardCode) return;
      if (!isOnline) return;

      const snapshot = pendingRef.current;
      const categoryIds =
        targetCategories && targetCategories.length > 0
          ? targetCategories
          : Object.keys(snapshot);

      const categoriesWithChanges = categoryIds.filter((categoryId) => {
        const entries = snapshot[categoryId];
        return entries && Object.keys(entries).length > 0;
      });

      if (categoriesWithChanges.length === 0) return true;

      setIsSavingMap((prev) => {
        const next = { ...prev };
        categoriesWithChanges.forEach((id) => {
          next[id] = true;
        });
        isSavingRef.current = next;
        return next;
      });

      const submission = buildSurveySubmission();

      if (!submission || Object.keys(submission.answers).length === 0) {
        clearSavingFlags(categoriesWithChanges);
        return;
      }

      try {
        await apiService.saveComplianceSurvey({
          organization_id: organizationId,
          standard: standardCode,
          questionnaireType: options.questionnaireType,
          compliance_score: submission.complianceScore,
          answers: submission.answers,
          scores: submission.scores,
          submitted_at: new Date().toISOString(),
        });

        const nextPending = { ...pendingRef.current };
        categoriesWithChanges.forEach((id) => {
          delete nextPending[id];
        });

        const timestamp = new Date().toISOString();
        const nextLastSaved = { ...lastSavedRef.current };
        categoriesWithChanges.forEach((id) => {
          nextLastSaved[id] = timestamp;
        });

        updateState({
          pending: nextPending,
          lastSavedAt: nextLastSaved,
        });

        if (typeof options.onSurveySaved === "function") {
          options.onSurveySaved();
        }
        clearSavingFlags(categoriesWithChanges);
        return true;
      } catch (error) {
        console.error("[Compliance] failed to autosave categories", error);
        return false;
      } finally {
        clearSavingFlags(categoriesWithChanges);
      }
    },
    [
      organizationId,
      standardCode,
      isOnline,
      buildSurveySubmission,
      updateState,
      options.onSurveySaved,
      options.questionnaireType,
      clearSavingFlags,
    ],
  );

  const setAnswer = useCallback(
    (categoryId: string, questionId: string, value: number) => {
      if (!categoryId || !questionId) return;
      const normalizedValue = Number.isFinite(value) ? Number(value) : 0;
      const currentValue =
        answersRef.current[categoryId]?.[questionId] ?? undefined;
      if (currentValue === normalizedValue) return;

      const timestamp = new Date().toISOString();
      const nextAnswers: AnswersByCategory = {
        ...answersRef.current,
        [categoryId]: {
          ...(answersRef.current[categoryId] || {}),
          [questionId]: normalizedValue,
        },
      };

      const nextPending: PendingMap = {
        ...pendingRef.current,
        [categoryId]: {
          ...(pendingRef.current[categoryId] || {}),
          [questionId]: { value: normalizedValue, updatedAt: timestamp },
        },
      };

      updateState({
        answers: nextAnswers,
        pending: nextPending,
      });
    },
    [updateState],
  );

  const registerCategories = useCallback(
    (
      categories: Array<{
        id: string;
        code?: string | null;
        name?: string | null;
        questions?: Array<{ id: string }>;
      }>,
    ) => {
      if (!categories || categories.length === 0) return;
      let changed = false;
      const nextAnswers: AnswersByCategory = { ...answersRef.current };
      const nextCategoryMap = { ...categoriesRef.current };

      categories.forEach((category) => {
        const existing = { ...(nextAnswers[category.id] || {}) };
        category.questions?.forEach((question) => {
          if (existing[question.id] == null) {
            existing[question.id] = 0;
            changed = true;
          }
        });
        nextAnswers[category.id] = existing;
        nextCategoryMap[category.id] = {
          id: category.id,
          code: category.code ?? null,
          name: category.name ?? null,
        };
      });

      categoriesRef.current = nextCategoryMap;

      if (changed) {
        updateState({ answers: nextAnswers });
      }
    },
    [updateState],
  );

  const saveAnswers = useCallback(
    async (categoryId?: string) => {
      if (!isOnline) return false;
      const categories = categoryId ? [categoryId] : undefined;
      return Boolean(await flushPending(categories));
    },
    [flushPending, isOnline],
  );

  const hydrateAnswers = useCallback(
    (grouped: AnswersByCategory) => {
      const pendingEntries = Object.values(pendingRef.current).some(
        (questions) => questions && Object.keys(questions).length > 0,
      );
      if (pendingEntries) return;
      updateState({ answers: grouped });
    },
    [updateState],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const pendingCategories = useMemo(
    () =>
      Object.entries(pendingRef.current)
        .filter(([, questions]) => Object.keys(questions).length > 0)
        .map(([categoryId]) => categoryId),
    [pending],
  );

  const hasUnsavedChanges = pendingCategories.length > 0;

  return {
    answers,
    setAnswer,
    saveAnswers,
    hydrateAnswers,
    registerCategories,
    isSaving: isSavingMap,
    lastSavedAt,
    pendingCategories,
    hasUnsavedChanges,
    isOnline,
  };
}

export default useComplianceAnswers;

